import React, { createContext, useContext, useState, useEffect } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const AUTH_TOKEN_KEY = "pattrnworks_admin_token";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

// Set REACT_APP_BYPASS_AUTH=true in .env ONLY for local development
// NEVER set to true in production!
const BYPASS_AUTH = process.env.REACT_APP_BYPASS_AUTH === 'true';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(BYPASS_AUTH);
  const [token, setToken] = useState<string | null>(BYPASS_AUTH ? "dev-bypass-token" : null);

  // Check if user has valid token on mount
  useEffect(() => {
    const storedToken = sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (storedToken) {
      verifyToken(storedToken);
    }
  }, []);

  const verifyToken = async (tokenToVerify: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/auth/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`
        }
      });
      
      const data = await response.json();
      
      if (data.valid) {
        setIsAuthenticated(true);
        setToken(tokenToVerify);
      } else {
        // Token invalid, clear it
        sessionStorage.removeItem(AUTH_TOKEN_KEY);
        setIsAuthenticated(false);
        setToken(null);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
      setIsAuthenticated(false);
      setToken(null);
    }
  };

  const login = async (password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await fetch(`${BACKEND_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (data.success && data.token) {
        setIsAuthenticated(true);
        setToken(data.token);
        sessionStorage.setItem(AUTH_TOKEN_KEY, data.token);
        return { success: true };
      } else {
        return { success: false, message: data.message || 'Invalid password' };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, message: 'Login failed. Please try again.' };
    }
  };

  const logout = async () => {
    if (token) {
      try {
        await fetch(`${BACKEND_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (error) {
        console.error('Logout request failed:', error);
      }
    }
    
    setIsAuthenticated(false);
    setToken(null);
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, token }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
